import { Component, Fragment, JSX } from "react";

import { UserContext, UserInfo } from "@app/user";
import { DashboardType } from "@app/user/enumerations";
import MegaMenuHeader from "./MegaMenuHeader";
import MegaMenuStandardLink from "./MegaMenuStandardLink";
import MenuItemEnum from "./menuItemEnum";

interface DashboardLookup {
  [index: string]: LinkInfo;
}

interface LinkInfo {
  link: string;
  icon: string;
  displayName: string;
}
//NOTE: Once a page is moved from 1.0 to 2.0, the MegaMenuStandardLink should be replaced with MegaMenuLink to prevent an unnecessary re-rendering of the whole page
class Dashboards extends Component {
  private readonly dashboardLookup: DashboardLookup = {};
  private readonly projectDashboardTypes: DashboardType[] = [];
  private readonly operationsDashboardTypes: DashboardType[] = [];

  constructor(props: never) {
    super(props);

    this.projectDashboardTypes.push(DashboardType.Project);
    this.projectDashboardTypes.push(DashboardType.NetworkAssessment);
    this.projectDashboardTypes.push(DashboardType.NYSDOHAdHocProject);
    this.projectDashboardTypes.push(DashboardType.CACDI);
    this.projectDashboardTypes.push(DashboardType.AccessReports);
    this.projectDashboardTypes.push(DashboardType.NetworkOutreach);

    this.operationsDashboardTypes.push(DashboardType.Operations);

    this.dashboardLookup[DashboardType.Project] = { displayName: "Standard Adequacy", icon: "list-alt", link: `${window.uiEndpoint}/Home?Filter=${DashboardType.Project}` };
    this.dashboardLookup[DashboardType.NetworkAssessment] = { displayName: "Network Assessment", icon: "list-alt", link: `/app/v2.0?Filter=${DashboardType.NetworkAssessment}` };
    this.dashboardLookup[DashboardType.CACDI] = { displayName: "California DOI", icon: "list-alt", link: `${window.uiEndpoint}/Home?Filter=${DashboardType.CACDI}` };
    this.dashboardLookup[DashboardType.NYSDOHAdHocProject] = { displayName: "Ad Hoc", icon: "list-alt", link: `${window.uiEndpoint}/Home?Filter=${DashboardType.NYSDOHAdHocProject}` };
    this.dashboardLookup[DashboardType.Directory] = { displayName: "Directory", icon: "list-alt", link: `${window.uiEndpoint}/Home?Filter=${DashboardType.Directory}` };
    this.dashboardLookup[DashboardType.Admin] = { displayName: "Admin", icon: "list-alt", link: `${window.uiEndpoint}/Home?Filter=${DashboardType.Admin}` };
    this.dashboardLookup[DashboardType.AccessReports] = { displayName: "Access Reports", icon: "list-alt", link: `/app/v2.0?Filter=${DashboardType.AccessReports}` };
    this.dashboardLookup[DashboardType.Operations] = { displayName: "Dashboard", icon: "cogs", link: `/app/v2.0?Filter=${DashboardType.Operations}` };
    this.dashboardLookup[DashboardType.NetworkOutreach] = { displayName: "Network Outreach", icon: "list-alt", link: `/app/v2.0?Filter=${DashboardType.NetworkOutreach}` };
  }

  public render() {
    return (
      <UserContext.Consumer>
        {(user) => this.getDashboards(user)}
      </UserContext.Consumer>
    );
  }

  private getDashboards = (user: UserInfo): JSX.Element => {
    if (user.authorizedDashboardTypes.length > 0) {
      // only render the "projects" menu if there are 1 or more "project" dashboards
      const userProjectDashboards = user.authorizedDashboardTypes.filter((x) => this.projectDashboardTypes.includes(x));
      const userOperationsDashboards = user.authorizedDashboardTypes.filter((x) => this.operationsDashboardTypes.includes(x));

      return (
        <>
          <ul className="menu">
            <li>
              <MegaMenuHeader title="Projects" />
              <ul className="menu">
                {
                  userProjectDashboards.map((dt) => (
                    this.getDashboardListItem(dt)
                  ))
                }
              </ul>
            </li>
          </ul>
          {
            userOperationsDashboards.length > 0
              ? (
                <ul className="menu">
                  <li>
                    <MegaMenuHeader title="Operations" />
                    <ul className="menu">
                      {
                        userOperationsDashboards.map((dt) => (
                          this.getDashboardListItem(dt)
                        ))
                      }
                    </ul>
                  </li>
                </ul>
              )
              : <></>
          }
        </>
      );
      // save so we can render non-project dashboards when the Big Wigs change their mind...
      // if the user is QUEST_ADMIN or CLIENT_ADMIN role, render the "Dashboards" menu for all the other authorized dashboards
      //const nonProjectDashboards = user.authorizedDashboardTypes.filter((x) => !userProjectDashboards.includes(x));
      //{nonProjectDashboards.length > 0 && (
      //  <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN]}>
      //    <MegaMenuHeader title="Dashboards" />
      //    {
      //      nonProjectDashboards.map((dt) => (
      //        this.getDashboardListItem(dt)
      //      ))
      //    }
      //  </RoleRestricted>
      //)
      //}
    }

    // otherwise return nothing
    return <Fragment />;
  }

  private getDashboardListItem = (dashboardType: DashboardType) => {
    const dashboardLookupInfo = this.dashboardLookup[dashboardType.toString()];

    let menuItem = MenuItemEnum.Unknown;
    switch (dashboardType) {
      case DashboardType.NetworkAssessment:
        menuItem = MenuItemEnum.CustomProject;
        break;
      case DashboardType.Project:
        menuItem = MenuItemEnum.Project;
        break;
      case DashboardType.Admin:
        menuItem = MenuItemEnum.Admin;
        break;
      case DashboardType.NYSDOHAdHocProject:
        menuItem = MenuItemEnum.Adhoc;
        break;
      case DashboardType.Directory:
        menuItem = MenuItemEnum.Directory;
        break;
      case DashboardType.CACDI:
        menuItem = MenuItemEnum.Cacdi;
        break;
      case DashboardType.AccessReports:
        menuItem = MenuItemEnum.AccessReports;
        break;
      case DashboardType.Operations:
        menuItem = MenuItemEnum.Operations;
        break;
      case DashboardType.NetworkOutreach:
        menuItem = MenuItemEnum.NetworkOutreach;
    }

    return (
      <MegaMenuStandardLink menuItem={menuItem} faIcon={dashboardLookupInfo.icon} key={dashboardLookupInfo.displayName} to={dashboardLookupInfo.link} linkText={dashboardLookupInfo.displayName} />
    );
  }
}

export default Dashboards;
